import {
  SESSION_STORAGE_KEYS,
  GROUP,
  IBF_ENABLED,
  SORT_ALPHABETICAL_ASCENDING,
  SORT_ALPHABETICAL_DESCENDING,
  SORT_DEFAULT,
  SORT_DURATION_ASCENDING,
  SORT_DURATION_DESCENDING,
  SORT_PRICE_ASCENDING,
  SORT_PRICE_DESCENDING,
  TYPE_OF_BOOKING,
  VENUE,
  EVENTSTEXT,
  OFF_THE_SHELF_PRODUCT,
  COUNTRIES,
  VIRTUAL,
  KEY_CODE,
  DEFAULT_CURRENCY,
  CostCentreInputType,
  DateTypeOptionIds,
  DateFieldMap,
  DATE_FORMAT,
  MyLearning_Link_Paths,
} from "utils/constants";
import {
  BookingJourneyStep,
  CreationMethod,
  DeliveryMethod,
  SortType,
  WorkflowStageEnum,
} from "utils/types";
import { ContentType, SortBy } from "./graphql/Global";
import portalSettingsManager from "utils/portalSettingsManager";
import { format, isAfter, isBefore, isEqual, startOfDay, startOfMonth, parse } from "date-fns";
import _ from "lodash";
import sanitizeHtml from "sanitize-html";
import {
  DelegateDetail,
  HostAndDelegates,
  ModificationStatus,
  ProductBasket,
  ProductDetails,
} from "store/bookingJourney";
import uuid from "react-uuid";
import {
  CompletedBookingEvent,
  PurchaseOrder,
  Venue,
  Delegate,
  Host,
} from "./graphql/bookingJourney/models";
import { Dispatch } from "react";
import { NIL as NIL_UUID } from "uuid";
import { UserContext } from "./graphql/UserContext";
import logger from "./logger/logger";

// Scheduling Status constants (lowercase for case-insensitive lookup)
const SCHEDULING_STATUS = {
  NEW_EXTERNAL_EVENT: "new external event",
  SUPPLIER_PENDING: "supplier pending",
  VENUE_PENDING: "venue pending",  
  PENDING: "pending",
  REQUESTOR_PENDING: "requestor pending",
  OCCUPANCY_PENDING: "occupancy pending",
  PROCESSED: "processed",
  CONFIRMED: "confirmed",
  DELIVERED: "delivered",
  CANCELLED: "cancelled",
  DELETED: "deleted",
  DECLINED: "declined",
  DIGITAL: "digital",
  ATTENDED: "attended",
  NO_SHOW: "no show",
  NEW: "new",
  PUBLIC_DISPLAY: "public display",
  PUBLIC_CANCELLED: "public cancelled",
  SWAPPED: "swapped",
  KPMG_PENDING: "kpmg pending"
} as const;

// LEAP Status constants
const LEAP_STATUS = {
  NEW: "New",
  IN_PROGRESS: "In Progress",
  CONFIRMED: "Confirmed",
  DELIVERED: "Delivered",
  CANCELLED: "Cancelled",
  DIGITAL: "Digital",
  ATTENDED: "Attended",
  DID_NOT_ATTEND: "Did not attend",
  NOT_APPLICABLE: "Not applicable",
} as const;

type SchedulingStatusValue = (typeof SCHEDULING_STATUS)[keyof typeof SCHEDULING_STATUS];
type LeapStatusValue = (typeof LEAP_STATUS)[keyof typeof LEAP_STATUS];

// Mapping from Scheduling Status to LEAP Status (grouped by outcome)
const schedulingStatusToLeapStatusMap: Record<SchedulingStatusValue, LeapStatusValue> = {
  [SCHEDULING_STATUS.NEW_EXTERNAL_EVENT]: LEAP_STATUS.NEW,
  [SCHEDULING_STATUS.SUPPLIER_PENDING]: LEAP_STATUS.IN_PROGRESS,
  [SCHEDULING_STATUS.VENUE_PENDING]: LEAP_STATUS.IN_PROGRESS,  
  [SCHEDULING_STATUS.REQUESTOR_PENDING]: LEAP_STATUS.IN_PROGRESS,
  [SCHEDULING_STATUS.PENDING]: LEAP_STATUS.IN_PROGRESS,
  [SCHEDULING_STATUS.OCCUPANCY_PENDING]: LEAP_STATUS.IN_PROGRESS,
  [SCHEDULING_STATUS.PROCESSED]: LEAP_STATUS.IN_PROGRESS,
  [SCHEDULING_STATUS.CONFIRMED]: LEAP_STATUS.CONFIRMED,
  [SCHEDULING_STATUS.DELIVERED]: LEAP_STATUS.DELIVERED,
  [SCHEDULING_STATUS.CANCELLED]: LEAP_STATUS.CANCELLED,
  [SCHEDULING_STATUS.DELETED]: LEAP_STATUS.CANCELLED,
  [SCHEDULING_STATUS.DECLINED]: LEAP_STATUS.CANCELLED,
  [SCHEDULING_STATUS.DIGITAL]: LEAP_STATUS.DIGITAL,
  [SCHEDULING_STATUS.ATTENDED]: LEAP_STATUS.ATTENDED,
  [SCHEDULING_STATUS.NO_SHOW]: LEAP_STATUS.DID_NOT_ATTEND,
  [SCHEDULING_STATUS.NEW]: LEAP_STATUS.NOT_APPLICABLE,
  [SCHEDULING_STATUS.PUBLIC_DISPLAY]: LEAP_STATUS.NOT_APPLICABLE,
  [SCHEDULING_STATUS.PUBLIC_CANCELLED]: LEAP_STATUS.NOT_APPLICABLE,
  [SCHEDULING_STATUS.SWAPPED]: LEAP_STATUS.NOT_APPLICABLE,
  [SCHEDULING_STATUS.KPMG_PENDING]: LEAP_STATUS.IN_PROGRESS
};

const mapSchedulingStatusToLeapStatus = (schedulingStatus: string | null | undefined): string => {
  if (!schedulingStatus) return "";
  const key = schedulingStatus.trim().toLowerCase() as SchedulingStatusValue;
  return schedulingStatusToLeapStatusMap[key] ?? "";
};

export const parseSortType = (
  sort: SortType,
): {
  sortBy: SortBy.NAME | SortBy.DURATION | SortBy.PRICE | SortBy.DEFAULT;
  isAscending: boolean;
} => {
  switch (sort) {
    case SORT_ALPHABETICAL_ASCENDING:
      return { sortBy: SortBy.NAME, isAscending: true };
    case SORT_ALPHABETICAL_DESCENDING:
      return { sortBy: SortBy.NAME, isAscending: false };
    case SORT_DURATION_ASCENDING:
      return { sortBy: SortBy.DURATION, isAscending: true };
    case SORT_DURATION_DESCENDING:
      return { sortBy: SortBy.DURATION, isAscending: false };
    case SORT_PRICE_ASCENDING:
      return { sortBy: SortBy.PRICE, isAscending: true };
    case SORT_PRICE_DESCENDING:
      return { sortBy: SortBy.PRICE, isAscending: false };
    case SORT_DEFAULT:
      return { sortBy: SortBy.DEFAULT, isAscending: true };
    default:
      return { sortBy: SortBy.NAME, isAscending: true };
  }
};

export const emailRegex =
  /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

export const removeSpaceAndSpecialCharacters = /[^A-Z0-9]/gi;

export const formInputSanitize = (input: string): string => {
  if (input) {
    input = input.replace(/(<([^>]+)>)/gi, "");
    return input.trim();
  }
  return input;
};

export const normalizeQuestionText = (htmlText?: string | null): string => {
  return (htmlText ?? "")
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
};

export const getContentType = (key: string): ContentType | undefined => {
  switch (key.toLowerCase()) {
    case "pathway":
      return ContentType.PATHWAY;
    case "channel":
      return ContentType.CHANNEL;
    case "course":
      return ContentType.COURSE;
    case "playlist":
      return ContentType.PLAYLIST;
    default:
      break;
  }
};

// returns date formatted in DD MMM, YYYY
// example: 24 Oct, 2020
export const mobileDateString = (date: Date): string => {
  const [, month, day, year] = date.toString().split(" ");
  return `${day} ${month}, ${year}`;
};

export const dueDateFormat = (date: string): string => {
  const lowercaseDate = date?.toLowerCase();
  if (lowercaseDate.indexOf("z") > -1 || lowercaseDate.indexOf("t") > -1) {
    date = lowercaseDate.split("t")?.[0];
  }
  const today = new Date(date);
  const day = String(today.getUTCDate()).padStart(2, "0");
  const month = String(today.getUTCMonth() + 1).padStart(2, "0");
  const year = today.getUTCFullYear().toString().substring(2);
  return `${month}.${day}.${year}`;
};

export const isValidDateString = (dateString: string | null | undefined): boolean => {
  if (!dateString || dateString.trim() === "") return false;
  const date = new Date(dateString);
  return !isNaN(date.getTime());
};

export const formatPreferredDate = (prefDate: string): string => {
  if (!prefDate || !isValidDateString(prefDate)) return "";
  return format(new Date(prefDate), DATE_FORMAT.ddddDMMMMyyyy);
};

export const formatTime = (inputTimeString = ""): string => {
  if (inputTimeString && inputTimeString.includes("hrs")) {
    const hrPrefix: string = inputTimeString.split(" ")[0];
    if (hrPrefix && Number(hrPrefix) && Number(hrPrefix) < 1) {
      const numberInMin = (Number(hrPrefix) * 60).toFixed(2);
      const decimalValue = (numberInMin + "").split(".")[1];
      return `${decimalValue === "00" ? parseFloat(numberInMin) : numberInMin} mins`;
    }
    return inputTimeString;
  }
  return inputTimeString;
};

export const openContactUs = (): void => {
  const {
    emailId,
    body = "",
    subject = "",
  } = portalSettingsManager.application?.common?.contactUs || {};
  const mailToUrl = `mailto:${emailId}?subject=${encodeURIComponent(
    subject,
  )}&body=${encodeURIComponent(body)}`;
  window.location.href = mailToUrl;
};

export const getDateFromDateTime = (datetime: string) => {
  if (datetime) {
    var date = new Date(datetime);
    var formattedDate = format(date, "dd MMMM yyyy");
    return formattedDate;
  }
};

export const getTimeFromDateTime = (datetime: string) => {
  if (datetime) {
    var time = new Date(datetime);
    var formattedTime = format(time, "H:mm a");
    return formattedTime;
  }
};

export const sortData = (sortType: string, data: any) => {
  const sortedContents = _.orderBy(
    data,
    [(item) => item.name.toLowerCase()],
    [sortType === SORT_ALPHABETICAL_DESCENDING ? "desc" : "asc"],
  );
  return sortedContents;
};

export const sortAnyData = (isAscending: boolean, data: any[], fieldName: string) => {
  if (!data?.length) return [];
  const sortedContents = _.orderBy(
    data,
    [
      (item) => {
        const value = item[fieldName];
        const dateValue = new Date(value);
        if (!isNaN(dateValue.getTime())) {
          return dateValue.getTime(); // Sort by full datetime
        }
        return typeof value === "string" ? value.toLowerCase() : value; // Sort string or fallback
      },
    ],
    [isAscending ? "asc" : "desc"],
  );

  return sortedContents;
};

export const getUniqueValuesFromArray = (data: any, propName) => {
  return data.reduce((acc, product) => {
    if (!acc.includes(product[propName]) && product[propName] && product[propName]?.trim()) {
      acc.push(product[propName]);
    }
    return acc;
  }, [] as string[]);
};

export const getUniqueValuesFromPrices = (data: any, propName) => {
  var prices = data.flatMap((item) => item["prices"]);
  let uniqueValues: string[] = [];
  if (prices && Array.isArray(prices) && prices.length > 0) {
    prices.map((element) => {
      if (element && element[propName]?.trim() && !uniqueValues.includes(element[propName])) {
        uniqueValues.push(element[propName]);
      }
    });
  }
  return uniqueValues;
};

export const getBookingTypeFromPrice = (data: any) => {
  const bookingType = getUniqueValuesFromPrices(data, "bookingType").map((x) =>
    constructBookingTypeFromProductType(x),
  );
  //exclude invalid bookingtype and return distinct elements from array
  return Array.from(new Set(bookingType.filter((n) => n)));
};

export const getUniqueValuesFromChildArray = (data: any, propName: string): string[] => {
  return data.reduce((acc, product) => {
    const value = product[propName];
    if (value && Array.isArray(value) && value.length > 0) {
      value.forEach((element) => {
        if (element && element.trim() && !acc.includes(element)) {
          acc.push(element);
        }
      });
    }
    return acc;
  }, [] as string[]);
};
export const ErrorToastPayload = (title: string, errorMessage: string) => {
  return { type: "error", title: title, message: errorMessage };
};

export const constructBookingTypeFromProductType = (productType: any) => {
  switch (productType) {
    case GROUP.PUBLIC:
      return TYPE_OF_BOOKING.Individual;
    case GROUP.CLOSED_CLIENT_ESTATE:
      return TYPE_OF_BOOKING.Group;
    case GROUP.CLOSED_COMMERCIAL_ESTATE:
      return TYPE_OF_BOOKING.Group;
  }
  return;
};

export const constructVenueFromProductType = (productType: any) => {
  switch (productType) {
    case GROUP.CLOSED_CLIENT_ESTATE:
      return VENUE.Your_own_venue;
    case GROUP.CLOSED_COMMERCIAL_ESTATE:
      return VENUE.Supplier_provides_venue;
  }
  return;
};

export const getproductTypeFromVenue = (venue: string) => {
  switch (venue) {
    case VENUE.Your_own_venue:
      return GROUP.CLOSED_CLIENT_ESTATE;
    case VENUE.Supplier_provides_venue:
      return GROUP.CLOSED_COMMERCIAL_ESTATE;
  }
  return;
};

export const hasVenue = (venue: string): boolean => {
  return VENUE.Your_own_venue === venue;
};

export const hasPurchaseOrder = (productDetail: ProductDetails): boolean => {
  return (
    !isBlank(productDetail.poNumber) ||
    !isBlank(productDetail.costCentre) ||
    !isBlank(productDetail.approvalId) ||
    !isBlank(productDetail.businessUnit)
  );
};

export const getDelegateString = (minimumDelegate: number, maximumDelegates: number) =>
  `${minimumDelegate} - ${maximumDelegates}`;

export const addOrUpdateSessionData = (item: { [key: string]: any }, storageKey: string) => {
  const dataFromStorage = sessionStorage.getItem(storageKey);
  let data: Array<{ [key: string]: any }> = [];

  if (dataFromStorage) {
    data = JSON.parse(dataFromStorage);
  }
  const existingItemIndex = data.findIndex((x) => x.key === item.key);
  if (existingItemIndex !== -1) {
    data[existingItemIndex] = { ...data[existingItemIndex], ...item };
  } else {
    data.push(item);
  }

  sessionStorage.setItem(storageKey, JSON.stringify(data));
};

export const getObjectByKey = (key: string, storageKey: string) => {
  const dataFromStorage = sessionStorage.getItem(storageKey);
  if (!dataFromStorage) {
    return null;
  }
  const data = JSON.parse(dataFromStorage);
  const existingItem = data.find((x) => key in x);

  if (existingItem) {
    return existingItem;
  } else {
    return null;
  }
};

export const removeHtmlTags = (text: string, allowedTags: string[]) => {
  return sanitizeHtml(text, {
    allowedTags: allowedTags,
    allowedAttributes: {},
  });
};

export const isIBFEnabled = (): boolean => {
  const ibfFromStorage = getObjectByKey(IBF_ENABLED, SESSION_STORAGE_KEYS.FEATURES);
  return ibfFromStorage != null
    ? ibfFromStorage[IBF_ENABLED]
    : portalSettingsManager?.application?.features?.enableIBFFlow;
};
export const isF2F = (product: ProductBasket) =>
  product.deliveryMethod === DeliveryMethod.F2FOnly ||
  product.deliveryMethod === DeliveryMethod.BlendedIncludingF2Fworkshop;

export const isAddressRequired = (product: ProductBasket) =>
  product.bookingType === TYPE_OF_BOOKING.Group &&
  isF2F(product) &&
  product.venue === VENUE.Your_own_venue;

export const NumberFormatter = (currency: string) =>
  Intl.NumberFormat("en-GB", { style: "currency", currency: currency || DEFAULT_CURRENCY });

// Basket can only hold a single currency; validates a new item's currency against it.
export const isCurrencyValidForBasket = (currency: string, basketCurrency: string): boolean => {
  if (!basketCurrency) return true;
  return (currency || DEFAULT_CURRENCY) === basketCurrency;
};

export const validateEmail = (email) => {
  return !emailRegex.test(String(email).toLowerCase());
};

export const validatePhone = (phone) => {
  const regex = /^[\+]?[0-9]{0,3}\W?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/;
  return !regex.test(String(phone).toLowerCase());
};

export const DateWithWeekdayYearMonthAndDay = (date) => {
  return new Date(date).toLocaleDateString("en-GB", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};
export const checkIsVirtual = (deliveryMethod) => {
  return (
    deliveryMethod !== DeliveryMethod.F2FOnly &&
    deliveryMethod !== DeliveryMethod.BlendedIncludingF2Fworkshop
  );
};

export const LIFECYCLE_STORAGE_KEY = "klp_learning_request_lifecycle";

export const setLearningRequestLifecycleLocal = (value?: string | null): void => {
  try {
    if (value) {
      localStorage.setItem(LIFECYCLE_STORAGE_KEY, value);
    } else {
      localStorage.removeItem(LIFECYCLE_STORAGE_KEY);
    }
  } catch (e) {
    logger.error("Failed to set lifecycle in localStorage", { error: e });
  }
};

export const getLearningRequestLifecycleLocal = (): string | null => {
  try {
    return localStorage.getItem(LIFECYCLE_STORAGE_KEY);
  } catch (e) {
    logger.error("Failed to read lifecycle from localStorage", { error: e });
    return null;
  }
};

export const clearLearningRequestLifecycleLocal = (): void => {
  try {
    localStorage.removeItem(LIFECYCLE_STORAGE_KEY);
  } catch (e) {
    logger.error("Failed to clear lifecycle from localStorage", { error: e });
  }
};

export const isStageTerminal = (stage: WorkflowStageEnum | null | undefined): boolean => {
  return (
    stage === WorkflowStageEnum.Completed ||
    stage === WorkflowStageEnum.Cancelled ||
    stage === WorkflowStageEnum.Failed ||
    stage === WorkflowStageEnum.FailedToSubmitToAppian
  );
};

export const isValidStage = (value: string | null): value is WorkflowStageEnum => {
  return value !== null && Object.values(WorkflowStageEnum).includes(value as WorkflowStageEnum);
};

export const trimAndLowercase = (item: any) => {
  return item?.toLocaleLowerCase().trim();
};

export const venueType = (bookingType?: string, isVirtual?: boolean) =>
  isPublicBooking(bookingType) ? false : isVirtual ? false : true;

export const isPublicBooking = (bookingType?: string) =>
  trimAndLowercase(bookingType) === trimAndLowercase(GROUP.PUBLIC);

export const isIBFPublicBooking = (bookingType?: string) =>
  trimAndLowercase(bookingType) === trimAndLowercase(TYPE_OF_BOOKING.Individual);

const populateDelegatesFromEvent = (event): DelegateDetail[] => {
  const { delegateDetails, eventCategory, sessionMinCapacity } = event;
  if (delegateDetails?.length > 0) {
    return delegateDetails.map((del: Delegate) => ({
      id: uuid(),
      delegateId: del.id,
      firstName: del.firstName,
      lastName: del.lastName,
      emailAddress: del.emailAddress,
    }));
  }

  const bookingType = constructBookingTypeFromProductType(eventCategory);
  const delegateCount =
    bookingType === TYPE_OF_BOOKING.Individual ? 1 : Number(sessionMinCapacity || 0);

  return Array.from({ length: delegateCount }, () => ({
    id: uuid(),
    firstName: "",
    lastName: "",
    emailAddress: "",
    isDeleted: false,
  }));
};

export const populateHostDelegatesFromEvent = (event) => {
  const hostAndDelegates: HostAndDelegates = {
    id: uuid(),
    hostId: event.host?.id ?? "",
    hostFirstName: event.host?.firstName ?? "",
    hostLastName: event.host?.lastName ?? "",
    hostEmailAddress: event.host?.emailAddress ?? "",
    hostPhoneNumber: event.host?.phoneNumber ?? "",
    areYouHost: event.host?.isBookerSameAsHost ?? false,
    delegateDetails: populateDelegatesFromEvent(event),
    hasDelegates: isPublicBooking(event.eventCategory)
      ? (event.isBookerDelegate ?? false)
      : event?.delegateDetails?.length > 0,
  };
  return hostAndDelegates;
};

export const populateProductDetailsFromEvents = (
  events,
  productDetails = null,
  preserveDetails = false,
) => {
  return events?.map((event) => {
    const existingDetails = productDetails?.find((p) => p.id === event.id);
    if (existingDetails && preserveDetails) {
      // When increasing quantity on the summary page, the event response returns all events without child table details. Only add the new product details.
      return existingDetails;
    }
    const paymentFields = splitPaymentFieldsFromCostCentre(event?.purchaseOrder?.costCentre);
    const updatedProductDetails: Partial<ProductDetails> = {
      id: event?.id,
      venueId: event?.venue?.id,
      dateType: event?.datetype,
      preferredStartTime: event?.agreedStartTime,
      location: event?.location ?? "",
      roomNumber: event?.venue?.roomNumber ?? "",
      address: event?.venue?.addressLine1 ?? "",
      hostAndDelegates: populateHostDelegatesFromEvent(event),
      poNumber: event?.purchaseOrder?.po,
      costCentre: paymentFields.costCentre,
      country: paymentFields.country,
      businessArea: paymentFields.businessArea,
      approvalId: event?.purchaseOrder?.approvalId,
      businessUnit: event?.purchaseOrder?.businessUnit,
      eventNotes: event?.eventNotes ?? "",
      poId: event?.purchaseOrder?.id,
      poUploadFileName: event?.purchaseOrder?.fileName,
      poUploadFileLink: event?.purchaseOrder?.link?.href,
      preferredDate: event?.agreedStartDate,
      alternativeDates: event?.alternativeDates,
    };
    const dateTypeMap = DateFieldMap[event?.dateType];
    //If mapping fails return updatedProductDetails without dates
    if (!dateTypeMap || dateTypeMap.dateTypeFieldConfig.length === 0) {
      return updatedProductDetails;
    }
    updatedProductDetails[dateTypeMap.dateTypeFieldConfig[0].field] = event?.agreedStartDate;
    if (event?.dateType === DateTypeOptionIds.DateRange) {
      updatedProductDetails[dateTypeMap.dateTypeFieldConfig[1].field] = event?.agreedEndDate;
    }
    return updatedProductDetails;
  });
};
export const populateEventFromProduct = (
  productBasket: ProductBasket,
  productDetails: ProductDetails,
  userContext?: UserContext,
) => {
  const eventCategory = productBasket.eventCategory;
  const venuType = venueType(eventCategory, checkIsVirtual(productBasket.deliveryMethod));
  const dateTypeMap = DateFieldMap[productDetails.dateType];

  return {
    id: productDetails.id,
    isDeleted:
      productBasket.modificationStatus === ModificationStatus.Deleted
        ? true
        : productDetails.modificationStatus === ModificationStatus.Deleted,
    activityId: productBasket.activityId,
    appianPriceId: productBasket.priceId,
    currency: productBasket.currency || DEFAULT_CURRENCY,
    country: COUNTRIES.UK,
    sessionMaxCapacity: productBasket.maximumDelegates,
    sessionMinCapacity: productBasket.minimumDelegates,
    anyNonStandardRequirements: false,
    contextualisationRequired: false,
    subscribeToNewsLetter: true,
    isProgramme: false,
    duration: productBasket.workshopLength?.toString(),
    eventChannel: productBasket.deliveryMethod,
    eventCategory: eventCategory,
    topicName: productBasket.title,
    cost: productBasket.price,
    topicId: productBasket.customerProductCode,
    team: EVENTSTEXT,
    virtualDelivery: checkIsVirtual(productBasket.deliveryMethod),
    location: checkIsVirtual(productBasket.deliveryMethod)
      ? !(isIBFPublicBooking(productBasket.bookingType) && isDigitalOnly(productBasket))
        ? VIRTUAL
        : ""
      : productDetails.location,
    eventNotes: productDetails.eventNotes,
    creationMethod: CreationMethod.Ibf,
    courseId: productBasket.courseId,
    venueType: venuType,
    eventSpecialised: OFF_THE_SHELF_PRODUCT,
    agreedStartDate: enableAlternativeDate()
      ? productDetails.preferredDate
      : dateTypeMap?.dateTypeFieldConfig?.length > 0
        ? productDetails[dateTypeMap?.dateTypeFieldConfig[0].field]
        : "",
    agreedEndDate:
      productDetails.dateType === DateTypeOptionIds.DateRange &&
      dateTypeMap?.dateTypeFieldConfig?.length > 1
        ? productDetails[dateTypeMap?.dateTypeFieldConfig[1].field]
        : "",
    dateType: productDetails.dateType,
    alternativeDates: productDetails.alternativeDates,
    agreedStartTime: productDetails.preferredStartTime,
    isBookerDelegate:
      isIBFPublicBooking(productBasket.bookingType) &&
      productDetails.hostAndDelegates?.hasDelegates,
    venue:
      hasVenue(productBasket.venue) && !checkIsVirtual(productBasket.deliveryMethod)
        ? populateVenue(productDetails)
        : null,
    host:
      productDetails.hostAndDelegates && !isIBFPublicBooking(productBasket.bookingType)
        ? populateHostDetails(productDetails, userContext)
        : null,
    delegateDetails: productDetails.hostAndDelegates
      ? populateDelegates(productDetails, productBasket.bookingType, userContext)
      : null,
    purchaseOrder:
      hasPurchaseOrder(productDetails) || !isBlank(productDetails.poId)
        ? populatePurchaseOrder(productDetails)
        : null,
  };
};

export const populateProductFromEvent = (eventGroup) => {
  return {
    id: uuid(),
    courseId: eventGroup[0].courseId,
    title: eventGroup[0].topicName,
    image: "",
    bookingType: constructBookingTypeFromProductType(eventGroup[0].eventCategory),
    customerProductCode: eventGroup[0].topicId,
    deliveryMethod: eventGroup[0].eventChannel,
    venue: constructVenueFromProductType(eventGroup[0].eventCategory),
    workshopLength: eventGroup[0].duration,
    minimumDelegates: eventGroup[0].sessionMinCapacity,
    maximumDelegates: eventGroup[0].sessionMaxCapacity,
    quantity: eventGroup.length,
    price: eventGroup[0].cost,
    currency: eventGroup[0].currency || DEFAULT_CURRENCY,
    activityId: eventGroup[0].activityId,
    subTotal: eventGroup[0].cost * eventGroup.length,
    productDetails: populateProductDetailsFromEvents(eventGroup),
    eventCategory: eventGroup[0].eventCategory,
    isValid: eventGroup[0].isValid,
    priceId: eventGroup[0].appianPriceId,
  };
};

const valueOrEmpty = (value: unknown): string => {
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  return "";
};

export const convertCompletedBookingToProductBasket = (
  booking: CompletedBookingEvent,
): ProductBasket => {
  const productDetails: ProductDetails = {
    id: booking.id,
    location: valueOrEmpty(booking.location),
    eventNotes: booking?.eventNotes,
    poNumber: valueOrEmpty(booking.purchaseOrder?.po),
    costCentre: valueOrEmpty(booking.purchaseOrder?.costCentre),
    approvalId: valueOrEmpty(booking.purchaseOrder?.approvalId),
    alternativeDates: valueOrEmpty(booking?.alternativeDates),
    businessUnit: valueOrEmpty(booking.purchaseOrder?.businessUnit),
    modificationStatus: null,
    dateType: booking.dateType || "",
    preferredStartTime: valueOrEmpty(booking.agreedStartTime),
    preferredDate: valueOrEmpty(booking.agreedStartDate),
    venueId: valueOrEmpty(booking.venue?.id),
    bookingId: booking.bookingId,
    bookingNo: booking.bookingNo,
    eventId: valueOrEmpty(booking.externalEventId || booking.eventId),
    dateEventRequested: booking.dateEventRequested,
    hostAndDelegates: {
      id: booking.id,
      hasDelegates: booking.delegateDetails?.length > 0 || false,
      hostFirstName: valueOrEmpty(booking.host?.firstName),
      hostLastName: valueOrEmpty(booking.host?.lastName),
      hostEmailAddress: valueOrEmpty(booking.host?.emailAddress),
      hostPhoneNumber: valueOrEmpty(booking.host?.phoneNumber),
      delegateDetails: (booking.delegateDetails || []).map((delegate) => ({
        id: delegate.emailAddress,
        firstName: delegate.firstName,
        lastName: delegate.lastName,
        emailAddress: delegate.emailAddress,
      })),
    },
  };

  const dateTypeMap = DateFieldMap[booking?.dateType];
  if (dateTypeMap?.dateTypeFieldConfig.length > 0) {
    productDetails[dateTypeMap.dateTypeFieldConfig[0].field] = booking?.agreedStartDate;
    if (booking?.dateType === DateTypeOptionIds.DateRange) {
      productDetails[dateTypeMap.dateTypeFieldConfig[1].field] = booking?.agreedEndDate;
    }
  }

  return {
    id: booking.id,
    courseId: booking.courseId || "",
    title: booking.topicName || "",
    image: "",
    bookingType: constructBookingTypeFromProductType(booking.eventCategory),
    customerProductCode: booking.topicId || "",
    deliveryMethod: booking.eventChannel || "",
    venue: constructVenueFromProductType(booking.eventCategory),
    workshopLength: booking.duration || null,
    minimumDelegates: booking.sessionMinCapacity || 0,
    maximumDelegates: booking.sessionMaxCapacity || 0,
    quantity: 1,
    price: booking.cost || 0,
    currency: booking.currency,
    subTotal: booking.cost || 0,
    activityId: booking.activityId,
    priceId: booking.appianPriceId,
    productDetails: [productDetails],
    modificationStatus: null,
    eventCategory: booking.eventCategory || "",
    bookingId: booking.bookingId,
    isValid: booking.isValid ?? false,
    eventDate: booking.confirmedStartDate,
    eventStatus: portalSettingsManager.application?.features?.showBookingStatus
      ? mapSchedulingStatusToLeapStatus(booking.eventStatus)
      : "",
  };
};

const populateHostDetails = (productDetails: ProductDetails, userContext: UserContext): Host => {
  return {
    id: productDetails.hostAndDelegates.hostId || NIL_UUID,
    eventId: productDetails.id,
    isBookerSameAsHost: productDetails.hostAndDelegates.areYouHost,
    firstName: productDetails.hostAndDelegates.areYouHost
      ? userContext?.userContext?.firstName
      : productDetails.hostAndDelegates.hostFirstName,
    lastName: productDetails.hostAndDelegates.areYouHost
      ? userContext?.userContext?.lastName
      : productDetails.hostAndDelegates.hostLastName,
    phoneNumber: productDetails.hostAndDelegates.areYouHost
      ? userContext?.userContext?.mobileNumber
        ? `${userContext?.userContext?.countryCode}${userContext?.userContext?.mobileNumber}`
        : ""
      : productDetails.hostAndDelegates.hostPhoneNumber,
    emailAddress: productDetails.hostAndDelegates.areYouHost
      ? userContext?.userContext?.learnerEmail
      : productDetails.hostAndDelegates.hostEmailAddress,
  };
};

const populateDelegates = (
  productDetails: ProductDetails,
  bookingType: string,
  userContext?: UserContext,
): Delegate[] => {
  if (isIBFPublicBooking(bookingType) && productDetails.hostAndDelegates.hasDelegates) {
    //for public booking there will be only one delegate,use that delegate details to update
    const existingBookerDelegate =
      productDetails.hostAndDelegates?.delegateDetails?.length > 0
        ? productDetails.hostAndDelegates?.delegateDetails[0]
        : null;

    return [
      {
        id: existingBookerDelegate?.delegateId ?? NIL_UUID,
        firstName: userContext?.userContext?.firstName,
        lastName: userContext?.userContext?.lastName,
        emailAddress: userContext?.userContext?.learnerEmail,
        isDeleted: false,
      },
    ];
  }

  return productDetails.hostAndDelegates?.delegateDetails
    ?.map((del: DelegateDetail) => {
      return del.modificationStatus === ModificationStatus.Added ||
        del.modificationStatus === ModificationStatus.Modified ||
        del.modificationStatus === ModificationStatus.Deleted
        ? {
            id: del.delegateId ?? NIL_UUID,
            isBookerSameAsHost: productDetails.hostAndDelegates.areYouHost,
            firstName: del.firstName,
            lastName: del.lastName,
            emailAddress: del.emailAddress,
            isDeleted: del.modificationStatus === ModificationStatus.Deleted,
          }
        : null;
    })
    .filter((p) => p);
};

export const checkSkip = (products: ProductBasket[], page?: BookingJourneyStep): boolean => {
  if (page == BookingJourneyStep.DateAndLocation) {
    return !products.some(
      (product) => !(isDigitalOnly(product) && isIBFPublicBooking(product.bookingType)),
    );
  }
  return false;
};

export const isDigitalOnly = (productBasket: ProductBasket): boolean => {
  return deliveryMethodComparison(productBasket, DeliveryMethod.DigitalOnly);
};

const deliveryMethodComparison = (event: ProductBasket, method: string) => {
  if (trimAndLowercase(event.deliveryMethod ?? "") === trimAndLowercase(method)) {
    return true;
  } else {
    return false;
  }
};

export const filterRequiredProducts = (products: ProductBasket[]) => {
  const filterArray = products.filter((product) => {
    return !(isDigitalOnly(product) && isIBFPublicBooking(product.bookingType));
  });
  return filterArray;
};

export const isValidDate = (date) => {
  return !isEqual(new Date(0).getTime(), new Date(date).getTime());
};
export const populateVenue = (productDetails: ProductDetails): Venue => {
  return {
    eventId: productDetails.id,
    id: productDetails.venueId,
    roomNumber: productDetails.roomNumber,
    city: productDetails.location,
    addressLine1: productDetails.address,
  };
};

export const getFormattedCostCentre = (
  productDetails: Pick<ProductDetails, "country" | "businessArea" | "costCentre">,
): string => {
  if (!enableCountryAndBusinessArea()) return productDetails.costCentre?.trim() || "";

  return [productDetails.country, productDetails.businessArea, productDetails.costCentre]
    .map((value) => (typeof value === "string" ? value.trim() : ""))
    .filter((value) => value !== "")
    .join("-");
};

export const populatePurchaseOrder = (productDetails: ProductDetails): PurchaseOrder => {
  const formattedCostCentre = getFormattedCostCentre(productDetails);

  return {
    eventId: productDetails.id,
    id: productDetails.poId,
    po: productDetails.poNumber ?? "",
    costCentre: formattedCostCentre,
    approvalId: productDetails.approvalId,
    businessUnit: productDetails.businessUnit,
    fileName: productDetails.poUploadFileName,
  };
};

export const dispatchSuccess = (dispatch: Dispatch<any>, t: any) => {
  dispatch({
    type: "alert/show",
    payload: {
      type: "success",
      title: t("basket"),
      message: t("basket_update_success"),
    },
  });
};

export const dispatchError = (dispatch: Dispatch<any>, t: any) => {
  dispatch({
    type: "alert/show",
    payload: ErrorToastPayload(t("basket"), t("basket_update_error")),
  });
};

export const isValidURL = (url: string): boolean => {
  try {
    new URL(url);
    return true;
  } catch (e) {
    return false;
  }
};

export const isBlank = (value: string): boolean => {
  return value === undefined || value === null || value === "";
};

export const createDateWithoutTime = (date: number | string | Date) =>
  new Date(date).setHours(0, 0, 0, 0);

export const IsDateAfter = (date, compareDate) =>
  isAfter(createDateWithoutTime(date), createDateWithoutTime(compareDate)) ||
  isEqual(createDateWithoutTime(date), createDateWithoutTime(compareDate));

export const IsDateBefore = (date, compareDate) =>
  isBefore(createDateWithoutTime(date), createDateWithoutTime(compareDate)) ||
  isEqual(createDateWithoutTime(date), createDateWithoutTime(compareDate));

export const setFocucByRef = (lastAnchorRef: any, startIndex: number) => {
  //Added to buy some time until DOM elements are loaded
  setTimeout(() => {
    if (lastAnchorRef.current && startIndex > 0) {
      lastAnchorRef.current.focus();
    }
  }, 0);
};

export const showCostCentre = (): boolean => {
  return portalSettingsManager?.application?.features?.enableCostCenter;
};

export const getCostCentreInputType = (): CostCentreInputType => {
  return (
    portalSettingsManager?.application?.features?.costCentreInputType ||
    CostCentreInputType.Freetext
  );
};

export const getCostCentreOptions = (businessUnit?: string): string[] => {
  const costCentres = portalSettingsManager?.dropdownData?.costCentres || [];
  if (!businessUnit) return costCentres.flatMap((c) => c.costCentres);
  return costCentres.find((c) => c.businessUnit === businessUnit)?.costCentres || [];
};

export const enablePoValidation = (): boolean => {
  return portalSettingsManager?.application?.features?.enablePOValidation;
};

export const getPONumberLength = (): number => {
  return portalSettingsManager?.application?.features?.poNumberLength || 9;
};

export const getPreferredDateMinimumWeeks = (): number => {
  const configuredWeeks = portalSettingsManager?.application?.features?.preferredDateMinimumWeeks;
  const parsedWeeks = Number(configuredWeeks);

  if (Number.isNaN(parsedWeeks)) {
    return 0;
  }

  return Math.floor(parsedWeeks);
};

export const getPreferredMinDate = (): Date => {
  const minDate = new Date();
  const minWeeks = getPreferredDateMinimumWeeks();

  if (minWeeks <= 0) {
    return minDate;
  }

  minDate.setDate(minDate.getDate() + minWeeks * 7);
  return minDate;
};

export const enablePOUpload = (): boolean => {
  return portalSettingsManager?.application?.features?.enablePOUpload || false;
};
export const enableApprovalId = (): boolean => {
  return portalSettingsManager?.application?.features?.enablePaymentApproval || false;
};

export const enableBusinessUnit = (): boolean => {
  return portalSettingsManager?.application?.features?.enableBusinessUnit || false;
};

export const enableCountryAndBusinessArea = (): boolean => {
  return portalSettingsManager?.application?.features?.enableCountryAndBusinessArea || false;
};

export const getCountryOptions = (): string[] => {
  const businessAreas = portalSettingsManager?.dropdownData?.businessAreasByCountry || [];
  return businessAreas.map((c) => c.country);
};

export const getBusinessAreaOptions = (country?: string): string[] => {
  const businessAreas = portalSettingsManager?.dropdownData?.businessAreasByCountry || [];
  if (!country || country.trim() === "") return [];
  var options = businessAreas.find((c) => c.country === country)?.businessArea || [];
  return options;
};

export const enableApplyToAll = (): boolean => {
  return portalSettingsManager?.application?.features?.enableApplyToAll || false;
};

export const enableInstructionBanner = (): boolean => {
  return portalSettingsManager?.application?.features?.enableBasketReminderMessage || false;
};

export const getMaxPOFileSizeInMB = (): number => {
  return portalSettingsManager?.application?.features?.maxPOFileSizeInMB || 2;
};

export const getAllowedPOFileTypes = (): string[] => {
  return portalSettingsManager?.application?.features?.acceptedPOFileTypes || ["application/pdf"];
};

export const handleMenuKeyDown = (
  e: {
    key: string;
    shiftKey: boolean;
    preventDefault: () => void;
    stopPropagation: () => void;
  },
  handleClose: () => void,
  visibleIds: any[],
  parentId: string,
  setSelectedIndex,
  findCurrentActiveElement,
) => {
  const length = visibleIds.length;
  switch (e.key.toUpperCase()) {
    case KEY_CODE.ESCAPE:
      handleClose();
      document.getElementById(parentId).focus();
      break;
    case KEY_CODE.ARROWUP:
      DecreaseSelectedIndex();
      break;
    case KEY_CODE.ARROWDOWN:
      IncreaseSelectedIndex();
      break;
    case KEY_CODE.TAB: {
      if (!e.shiftKey) {
        IncreaseSelectedIndex();
      } else {
        DecreaseSelectedIndex();
      }
      break;
    }
    case KEY_CODE.HOME:
      setSelectedIndex(0);
      e.preventDefault();
      break;
    case KEY_CODE.END:
      setSelectedIndex(length - 1);
      e.preventDefault();
      break;
    case KEY_CODE.ENTER:
    case KEY_CODE.SPACE: {
      const element = findCurrentActiveElement();
      if (element) {
        element.click();
        e.preventDefault();
        e.stopPropagation();
      }
      break;
    }
    default:
      break;
  }

  function IncreaseSelectedIndex() {
    setSelectedIndex((prev) => (prev + 1) % length);
    e.preventDefault();
  }

  function DecreaseSelectedIndex() {
    setSelectedIndex((prev) => (prev - 1 + length) % length);
    e.preventDefault();
  }
};

export const handleEscapeKey = (
  callback: () => void,
  options?: {
    focusElement?: HTMLElement | null;
  },
) => {
  return (e: KeyboardEvent) => {
    if (e.key.toUpperCase() === KEY_CODE.ESCAPE) {
      callback();
      if (options?.focusElement) {
        options.focusElement.focus();
      }
    }
  };
};

export const getJsonPropertyOrString = (input: string, property: string) => {
  if (!input) {
    return null;
  }
  try {
    const parsed = JSON.parse(input);
    // If the parsed result is a non-null object and has the property, return its value.
    if (typeof parsed === "object" && parsed !== null && property in parsed) {
      return parsed[property];
    } else logger.error("Property doesn't exist in JSON");
  } catch {}
  return input;
};
export const isDateLessThanMin = (date, minDate, isMonthView) => {
  if (!date) {
    return false;
  }
  const startOfPeriod = isMonthView ? startOfMonth : startOfDay;

  // Set the time component to the start of the relevant period (Month or Day)
  const dateToCompare = startOfPeriod(date);
  const minToCompare = startOfPeriod(minDate);

  return isBefore(dateToCompare, minToCompare);
};

export const areDatesValid = (productDetails: ProductDetails, minDate: Date): boolean => {
  const dateFieldConfigs = DateFieldMap[productDetails.dateType]?.dateTypeFieldConfig || [];

  if (dateFieldConfigs.length === 0) {
    return false;
  }
  const isInvalid = dateFieldConfigs.some(
    (fieldConfig) =>
      !productDetails?.[fieldConfig.field] ||
      isDateLessThanMin(productDetails[fieldConfig.field], minDate, fieldConfig.isMonth),
  );

  return !isInvalid;
};
export const isDateRangeValid = (productDetails: ProductDetails): boolean => {
  const isDateRangeType = productDetails.dateType === DateTypeOptionIds.DateRange;

  if (!isDateRangeType) {
    return true;
  }

  const dateFieldConfigs = DateFieldMap[productDetails.dateType].dateTypeFieldConfig || [];

  if (dateFieldConfigs.length < 2) {
    return true; // Not enough fields for a range check
  }

  const fromDateField = dateFieldConfigs[0].field;
  const toDateField = dateFieldConfigs[1].field;

  const fromDateString = productDetails[fromDateField];
  const toDateString = productDetails[toDateField];

  if (fromDateString && toDateString) {
    const fromDate = parse(fromDateString, DATE_FORMAT.EEEMMMddyyyy, new Date());
    const toDate = parse(toDateString, DATE_FORMAT.EEEMMMddyyyy, new Date());

    return !isAfter(fromDate, toDate);
  }
  return true;
};

export const handleNewLearningRequestClick = (
  onOpenNewLearningStepSurvey: () => void,
  handleNavigate: (path: string) => void,
) =>
  portalSettingsManager.application.features.enableNewLearningRequest
    ? onOpenNewLearningStepSurvey()
    : handleNavigate(MyLearning_Link_Paths.LearningRequest);

export const enableAlternativeDate = () => {
  return process.env.REACT_APP_ENABLE_ALTERNATIVE_DATE === "true";
};

export const isWithinInterval = (date: Date, interval: { start: Date; end: Date }): boolean => {
  if (!date || !interval?.start || !interval?.end) {
    return false;
  }
  const dateToCheck = startOfDay(date);
  const intervalStart = startOfDay(interval.start);
  const intervalEnd = startOfDay(interval.end);

  return (
    (dateToCheck >= intervalStart || isEqual(dateToCheck, intervalStart)) &&
    (dateToCheck <= intervalEnd || isEqual(dateToCheck, intervalEnd))
  );
};
export const getEffectiveValue = (
  localState: Record<string, string> | null,
  detailId: string,
  fallback: string | undefined | null,
): string | undefined | null => {
  if (localState !== null && localState[detailId] !== undefined) return localState[detailId];
  return fallback;
};

export const extractLastInteger = (value: string | null | undefined, splitter = "-"): string => {
  if (!value || typeof value !== "string") return "";

  const segments = value
    .split(splitter)
    .map((segment) => segment.trim())
    .filter(Boolean);

  if (!segments.length) return "";

  for (let i = segments.length - 1; i >= 0; i--) {
    if (/^\d+$/.test(segments[i])) {
      return segments[i];
    }
  }

  return "";
};

/**
 * Splits a combined cost centre string back into country, businessArea, and costCentre.
 * Supports formats: "DL-1234567" (country-costCentre) and "UK-Life-1234567" (country-businessArea-costCentre)
 */
export const splitPaymentFieldsFromCostCentre = (
  formattedValue: string | undefined | null,
): { country?: string; businessArea?: string; costCentre: string } => {
  const raw = _.trim(formattedValue || "");
  if (!raw) return { costCentre: "" };
  if (!enableCountryAndBusinessArea()) return { costCentre: raw };

  const config = portalSettingsManager?.dropdownData?.businessAreasByCountry || [];
  const parts = _.compact(_.map(raw.split("-"), _.trim));
  const rawLower = raw.toLowerCase();

  // Match country from config
  const countryOpt = _.find(config, (c) =>
    rawLower.startsWith(`${_.toLower(_.trim(c?.country))}-`),
  );

  if (!countryOpt) {
    // Fallback: parse by position when config unavailable
    if (parts.length <= 1) return { costCentre: raw };
    if (parts.length === 2) return { country: parts[0], costCentre: parts[1] };
    return {
      country: parts[0],
      businessArea: parts.slice(1, -1).join("-"),
      costCentre: _.last(parts) || "",
    };
  }

  const country = _.trim(countryOpt.country);
  const rest = _.trim(raw.slice(country.length + 1));
  const restLower = rest.toLowerCase();

  // Match business area (longest first to handle hyphenated names)
  const areas = _.orderBy(_.compact(_.map(countryOpt.businessArea, _.trim)), "length", "desc");
  const matchedArea = _.find(areas, (area) => restLower.startsWith(`${_.toLower(area)}-`));

  if (matchedArea) {
    return {
      country,
      businessArea: matchedArea,
      costCentre: _.trim(rest.slice(matchedArea.length + 1)) || rest,
    };
  }

  return { country, costCentre: rest };
};
